import {
  NioshDto,
  NioshLiftEvaluationDto,
} from "src/app/services/generatedApi";
import {
  getHorizontalMultiplier,
  getVerticalMultiplier,
  getDistanceMultiplier,
  getAsymmetricMultiplier,
  getFrequencyMultiplier,
  getCouplingMultiplier,
  NioshCouplingQuality,
} from "./constants";

export interface NioshCalculationResult {
  origin: {
    rwl: number;
    liftingIndex: number;
    multipliers: {
      lc: number;
      hm: number;
      vm: number;
      dm: number;
      am: number;
      fm: number;
      cm: number;
    };
  };
  destination: {
    rwl: number;
    liftingIndex: number;
    multipliers: {
      lc: number;
      hm: number;
      vm: number;
      dm: number;
      am: number;
      fm: number;
      cm: number;
    };
  };
  overallLiftingIndex: number;
  riskLevel: "ACCEPTABLE" | "MODERATE_RISK" | "HIGH_RISK";
}

export function calculateNioshAssessment(
  data: NioshDto,
): NioshCalculationResult {
  const verticalTravelDistanceIn = Math.abs(
    data.origin.verticalHeightIn - data.destination.verticalHeightIn,
  );
  const originResult = calculateSinglePosition(
    data.origin,
    verticalTravelDistanceIn,
  );
  const destinationResult = calculateSinglePosition(
    data.destination,
    verticalTravelDistanceIn,
  );

  const overallLiftingIndex = Math.max(
    originResult.liftingIndex,
    destinationResult.liftingIndex,
  );

  const riskLevel = getRiskLevel(overallLiftingIndex);

  return {
    origin: originResult,
    destination: destinationResult,
    overallLiftingIndex,
    riskLevel,
  };
}

function calculateSinglePosition(
  evaluation: NioshLiftEvaluationDto,
  verticalTravelDistanceIn: number,
) {
  const LC = 51;

  const HM = getHorizontalMultiplier(evaluation.horizontalDistanceIn);
  const VM = getVerticalMultiplier(evaluation.verticalHeightIn);
  const DM = getDistanceMultiplier(verticalTravelDistanceIn);
  const AM = getAsymmetricMultiplier(evaluation.asymmetryAngleDeg);

  const durationHours =
    evaluation.durationCategory === 1
      ? 0.5
      : evaluation.durationCategory === 2
        ? 1.5
        : 4;

  const FM = getFrequencyMultiplier(
    evaluation.liftFrequencyPerMin,
    durationHours,
    evaluation.verticalHeightIn,
  );

  const CM = getCouplingMultiplier(
    evaluation.coupling as NioshCouplingQuality,
    evaluation.verticalHeightIn,
  );

  const rwl = LC * HM * VM * DM * AM * FM * CM;

  const liftingIndex = evaluation.loadWeightLb / rwl;

  return {
    rwl: parseFloat(rwl.toFixed(2)),
    liftingIndex: parseFloat(liftingIndex.toFixed(3)),
    multipliers: {
      lc: LC,
      hm: parseFloat(HM.toFixed(3)),
      vm: parseFloat(VM.toFixed(3)),
      dm: parseFloat(DM.toFixed(3)),
      am: parseFloat(AM.toFixed(3)),
      fm: parseFloat(FM.toFixed(3)),
      cm: parseFloat(CM.toFixed(3)),
    },
  };
}

function getRiskLevel(
  liftingIndex: number,
): "ACCEPTABLE" | "MODERATE_RISK" | "HIGH_RISK" {
  if (liftingIndex <= 1.0) return "ACCEPTABLE";
  if (liftingIndex <= 3.0) return "MODERATE_RISK";
  return "HIGH_RISK";
}

export function getRiskDescription(riskLevel: string): string {
  switch (riskLevel) {
    case "ACCEPTABLE":
      return "This lift may be acceptable";
    case "MODERATE_RISK":
      return "This lift may increase the risk of low back or lifting injury. Controls should be considered";
    case "HIGH_RISK":
      return "This lift may exceed the capabilities of safely performing the lift for nearly all workers. Redesign of the lifting task is recommended";
    default:
      return "";
  }
}

export function getRiskColor(riskLevel: string): string {
  switch (riskLevel) {
    case "ACCEPTABLE":
      return "#4caf50";
    case "MODERATE_RISK":
      return "#ff9800";
    case "HIGH_RISK":
      return "#f44336";
    default:
      return "#9e9e9e";
  }
}
